import { Campaign, CampaignWithSessionCount } from './types/campaign';
import api from './';
import { AxiosResponse } from 'axios';
import { PaginatedResponse } from './types/pagination';
import { PlayerCharacter } from './types/playerCharacter';
import { SpellingsConfig } from './types/spellings';

const fetchCampaigns = async (limit = 10, page = 1, sortBy?: string, active?: boolean) => {
  const params: { limit: number; page: number; sortBy?: string; active?: string } = { limit, page };
  if (sortBy) {
    params.sortBy = sortBy;
  }
  if (active !== undefined) {
    params.active = active.toString();
  }

  return await api.get<
    {
      limit: number;
      page: number;
      sortBy?: string;
      active?: string;
    },
    AxiosResponse<PaginatedResponse<CampaignWithSessionCount>>
  >('/campaigns', {
    params
  });
};

const fetchCampaignById = async (id: string) => {
  return await api.get<unknown, AxiosResponse<CampaignWithSessionCount>>(`/campaigns/${id}`);
};

const createCampaign = async (campaignData: Partial<Campaign>) => {
  return await api.post<Partial<Campaign>, AxiosResponse<Campaign>>('/campaigns', campaignData);
};

const updateCampaign = async (id: string, campaignData: Partial<Campaign>) => {
  return await api.patch<Partial<Campaign>, AxiosResponse<Campaign>>(
    `/campaigns/${id}`,
    campaignData
  );
};

const deleteCampaign = async (id: string) => {
  return await api.delete<unknown, AxiosResponse<void>>(`/campaigns/${id}`);
};

const fetchActiveCampaigns = async () => {
  return await api.get<unknown, AxiosResponse<Campaign[]>>('/campaigns/active/list');
};

const fetchInactiveCampaigns = async () => {
  return await api.get<unknown, AxiosResponse<Campaign[]>>('/campaigns/inactive/list');
};

const fetchCampaignPlayers = async (id: string, debugSource?: string) => {
  return await api.get<unknown, AxiosResponse<PlayerCharacter[]>>(`/campaigns/${id}/players`, {
    headers: debugSource ? { 'X-Debug-Source': debugSource } : undefined
  });
};

const activateCampaign = async (id: string) => {
  return await api.patch<unknown, AxiosResponse<Campaign>>(`/campaigns/${id}/activate`);
};

const deactivateCampaign = async (id: string) => {
  return await api.patch<unknown, AxiosResponse<Campaign>>(`/campaigns/${id}/deactivate`);
};

const updatePlayerCharacter = async (id: string, playerData: Partial<PlayerCharacter>) => {
  const response = await api.patch<
    Partial<PlayerCharacter>,
    AxiosResponse<{ ok: boolean; data: PlayerCharacter; error?: string }>
  >(`/player-characters/${id}`, playerData);

  if (!response.data.ok) {
    throw new Error(response.data.error ?? 'Failed to update player character');
  }

  return response.data.data;
};

const generatePlayerCharacterImageUploadUrl = async (
  id: string,
  fileName: string,
  mimeType: string,
  fileSize?: number
) => {
  const response = await api.post<
    { fileName: string; mimeType: string; fileSize?: number },
    AxiosResponse<{
      ok: boolean;
      data: { uploadUrl: string; key: string; expiresIn: number };
      error?: string;
    }>
  >(`/player-characters/${id}/upload-url`, { fileName, mimeType, fileSize });

  if (!response.data.ok) {
    throw new Error(response.data.error ?? 'Failed to generate upload URL');
  }

  return response.data.data;
};

const createPlayerCharacter = async (
  campaignId: string,
  playerData: {
    characterName: string;
    realLifeName?: string;
    characterDescription?: string;
    userId?: string | null;
  }
) => {
  return await api.post<
    {
      characterName: string;
      realLifeName?: string;
      characterDescription?: string;
      userId?: string | null;
    },
    AxiosResponse<PlayerCharacter>
  >(`/campaigns/${campaignId}/players`, playerData);
};

const retirePlayerCharacter = async (id: string) => {
  const response = await api.post<
    unknown,
    AxiosResponse<{ ok: boolean; data: PlayerCharacter; error?: string }>
  >(`/player-characters/${id}/retire`);

  if (!response.data.ok) {
    throw new Error(response.data.error ?? 'Failed to retire player character');
  }

  return response.data.data;
};

const unretirePlayerCharacter = async (id: string) => {
  const response = await api.post<
    unknown,
    AxiosResponse<{ ok: boolean; data: PlayerCharacter; error?: string }>
  >(`/player-characters/${id}/unretire`);

  if (!response.data.ok) {
    throw new Error(response.data.error ?? 'Failed to unretire player character');
  }

  return response.data.data;
};

const fetchCampaignSpellings = async (id: string) => {
  return await api.get<
    unknown,
    AxiosResponse<{ campaign: SpellingsConfig; combined: SpellingsConfig }>
  >(`/campaigns/${id}/spellings`);
};

const updateCampaignSpellings = async (id: string, data: SpellingsConfig) => {
  return await api.put<typeof data, AxiosResponse<SpellingsConfig>>(
    `/campaigns/${id}/spellings`,
    data
  );
};

const getCoverImageUploadUrl = async (campaignId: string, fileName: string, mimeType: string) => {
  const response = await api.post<
    { fileName: string; mimeType: string },
    AxiosResponse<{ uploadUrl: string; key: string; expiresIn: number }>
  >(`/campaigns/${campaignId}/cover-image/upload-url`, { fileName, mimeType });
  return response.data;
};

const confirmCoverImageUpload = async (
  campaignId: string,
  key: string
): Promise<CampaignWithSessionCount> => {
  const response = await api.patch<{ key: string }, AxiosResponse<CampaignWithSessionCount>>(
    `/campaigns/${campaignId}/cover-image`,
    { key }
  );
  return response.data;
};

const deleteCoverImage = async (campaignId: string) => {
  await api.delete(`/campaigns/${campaignId}/cover-image`);
};

const CampaignsApi = {
  fetchCampaigns,
  fetchCampaignById,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  fetchActiveCampaigns,
  fetchInactiveCampaigns,
  fetchCampaignPlayers,
  activateCampaign,
  deactivateCampaign,
  updatePlayerCharacter,
  generatePlayerCharacterImageUploadUrl,
  createPlayerCharacter,
  retirePlayerCharacter,
  unretirePlayerCharacter,
  fetchCampaignSpellings,
  updateCampaignSpellings,
  getCoverImageUploadUrl,
  confirmCoverImageUpload,
  deleteCoverImage
};

export default CampaignsApi;
